"use client";

import { useState } from "react";
import { PenSquare, ArrowRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useAdminOverlay } from "./admin-overlay-context";
import { SectionRegenerateButton } from "./section-regenerate-button";
import { StakesMockupsRegenerateButton } from "./stakes-mockups-regenerate-button";
import { getAdminSection, FIXED_SECTIONS, type AdminSectionKey } from "./section-generators";

type SidebarItem =
  | { kind: "generate"; id: string; sectionKey: AdminSectionKey; withMockups?: boolean }
  | { kind: "fixed"; id: string };

// Ordre exact des sections telles que rendues dans `[slug]/page.tsx`.
const SIDEBAR_ITEMS: SidebarItem[] = [
  { kind: "generate", id: "presentation", sectionKey: "hero" },
  { kind: "generate", id: "presentation", sectionKey: "intro" },
  { kind: "generate", id: "enjeux", sectionKey: "enjeux", withMockups: true },
  { kind: "fixed", id: "methode" },
  { kind: "fixed", id: "confiance" },
  { kind: "generate", id: "tarifs", sectionKey: "tarifs" },
  { kind: "generate", id: "reference", sectionKey: "reference" },
  { kind: "fixed", id: "reserver" },
  { kind: "generate", id: "faq", sectionKey: "faq" },
];

// Panneau d'admin : bouton flottant (bas gauche, distinct de la pastille de
// réservation en bas droite et du sommaire fixé à gauche sur grand écran) qui
// ouvre la liste complète des sections, dans l'ordre de la page, avec un
// résumé de leur contenu actuel et un accès direct à la régénération.
export function AdminSidebar() {
  const { data } = useAdminOverlay();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Panneau d'admin : sections de la page"
        className="fixed bottom-6 left-6 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full border border-paper/20 bg-navy text-paper shadow-2xl shadow-navy/40 transition-colors hover:bg-gold hover:text-navy hover:border-gold"
      >
        <PenSquare className="h-5 w-5" strokeWidth={2} />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Sections de la page</SheetTitle>
            <SheetDescription>
              Dans l&apos;ordre d&apos;affichage. Déplie une section pour voir son contenu
              actuel et la régénérer.
            </SheetDescription>
          </SheetHeader>

          <Accordion type="multiple" className="px-4 pb-6">
            {SIDEBAR_ITEMS.map((item, i) => {
              if (item.kind === "fixed") {
                const fixed = FIXED_SECTIONS.find((f) => f.id === item.id);
                if (!fixed) return null;
                return (
                  <AccordionItem key={`${item.id}-${i}`} value={`${item.id}-${i}`}>
                    <AccordionTrigger>{fixed.label}</AccordionTrigger>
                    <AccordionContent className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Contenu fixe (pas d&apos;IA), à éditer dans{" "}
                        <code className="font-mono text-xs">{fixed.file}</code>.
                      </p>
                      <a
                        href={`#${item.id}`}
                        onClick={() => setOpen(false)}
                        className="inline-flex items-center gap-1 text-xs text-gold underline underline-offset-2"
                      >
                        Aller à la section
                        <ArrowRight className="h-3 w-3" />
                      </a>
                    </AccordionContent>
                  </AccordionItem>
                );
              }

              const descriptor = getAdminSection(item.sectionKey);
              return (
                <AccordionItem key={`${item.sectionKey}-${i}`} value={`${item.sectionKey}-${i}`}>
                  <AccordionTrigger>{descriptor.label}</AccordionTrigger>
                  <AccordionContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">{descriptor.summarize(data)}</p>
                    <div className="flex items-center gap-2">
                      <SectionRegenerateButton sectionKey={item.sectionKey} />
                      {item.withMockups && <StakesMockupsRegenerateButton />}
                      <a
                        href={`#${item.id}`}
                        onClick={() => setOpen(false)}
                        className="inline-flex items-center gap-1 text-xs text-gold underline underline-offset-2"
                      >
                        Aller à la section
                        <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </SheetContent>
      </Sheet>
    </>
  );
}
